import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'What columns do I need in a dividend tracking spreadsheet?',
    a: 'The bare minimum is eight: Ticker, Shares, Cost Basis per Share, Total Cost, Current Price, Current Value, Annual Dividend Rate, and Annual Income. Add Yield (Annual Income / Current Value), Yield on Cost (Annual Income / Total Cost), Payout Frequency, and Sector and you have a fully functional tracker that competes with most paid apps.',
  },
  {
    q: 'Can I get live stock prices in Google Sheets for free?',
    a: 'Yes — Google Sheets has a built-in GOOGLEFINANCE() function. Use =GOOGLEFINANCE("KO", "price") for the current price, =GOOGLEFINANCE("KO", "yieldpct") for yield. There’s a 20-minute delay on stocks and the data quality is okay-not-great. Excel has STOCKHISTORY() and Linked Data Stock Types (Microsoft 365 only) which are more reliable. Both stop short on dividend frequency, ex-dates, payout ratio, and dividend history.',
  },
  {
    q: 'How do I track dividend income over time in a spreadsheet?',
    a: 'Add a separate tab called "Dividend Log" with columns Date, Ticker, Shares Held, Dividend per Share, Total Received, Source Account (Taxable / Roth / IRA). Every time a dividend lands, add a row. Use SUMIFS() to tally year-to-date income, monthly totals, and per-ticker totals. The downside: you have to manually log every payment, and ex-dividend / pay date mismatches make it easy to drop or double-count entries.',
  },
  {
    q: 'What’s the biggest spreadsheet gotcha?',
    a: 'Dividend rate stale-ness. GOOGLEFINANCE returns the dividend that was last paid — it doesn’t know about announced raises. A company that just raised from $1.00 to $1.10 still shows $1.00 in your sheet for 3 months until the next pay-date prints. Your forward annual income column will be silently wrong. Sheet-only trackers also miss special dividends, stock splits, and dividend cuts unless you notice and update manually.',
  },
  {
    q: 'When is a spreadsheet enough vs when do I outgrow it?',
    a: 'A spreadsheet is great for 1–5 holdings or under $20K invested. It breaks down when you have 10+ tickers (every announcement requires a manual update), multiple account types (the cross-account math gets ugly), or want forward-looking features like ex-dividend alerts, dividend triangle scores, or income forecasts. At that point DiviDrip’s free dashboard does in 30 seconds what takes 30 minutes in a sheet.',
  },
  {
    q: 'Can I import my existing spreadsheet into DiviDrip?',
    a: 'Yes \u2014 two ways. Easiest: download the DiviDrip .xlsx template from the Portfolio dashboard\'s tools menu, paste your holdings into the Holdings tab (Ticker in column A, Shares in C, Cost/Share in D, optional Purchase Date in P), then drop the .xlsx back into the Capital Tracker card\'s "Import CSV / XLSX" button \u2014 the same modal accepts both formats. Alternative: export your existing Google Sheet or Excel workbook to .csv and upload that. The modal previews every row and lets you choose Append vs Overwrite before commit.',
  },
];

export default function LearnTrackInSpreadsheet() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="How to Track Your Dividend Income in a Spreadsheet (Google Sheets &amp; Excel, 2026) | DiviDrip"
      description="A complete spreadsheet template for tracking dividend income. Required columns, live-price formulas, GOOGLEFINANCE vs Excel Stock Types, monthly income logs, and where spreadsheets stop being enough."
      canonical="https://twylightcrow.com/learn/track-dividend-income-spreadsheet"
      guidePath="/learn/track-dividend-income-spreadsheet"
      sectionLabel="Practical Workflows"
      sectionAnchor="workflows"
      kicker="Learn · Spreadsheet workflow"
      h1="How to Track Your Dividend Income in a Spreadsheet"
      datePublished="2026-06-14"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/what-is-a-dividend', label: 'What is a dividend? A complete beginner’s guide' },
        { to: '/learn/yield-on-cost', label: 'Yield on cost — your real long-term yield' },
        { to: '/learn/monthly-dividend-calendar', label: 'The monthly dividend calendar trick' },
        { to: '/learn/dividend-calendar-months', label: 'Which months pay the most? The calendar breakdown' },
        { to: '/learn/first-10-tickers', label: 'The first 10 tickers — starting with $10K' },
        { to: '/learn/dividend-triangle', label: 'Reading the Dividend Triangle score' },
      ]}
    >
      <p>
        If you have a small dividend portfolio and don&apos;t want to commit to
        another app, a spreadsheet is a perfectly serious tracking tool. With
        an hour of setup it gives you current value, forward annual income,
        yield on cost, and monthly cash-flow history — everything most paid
        dividend trackers charge for.
      </p>
      <p>
        This guide walks you through the columns to use, the live-price
        formulas, how to log incoming dividends, and — honestly — the
        point where you outgrow a sheet and a real tracker becomes worth it.
      </p>

      <h2>The 8-column Holdings tab</h2>
      <p>
        Open a new Google Sheet (or Excel workbook) and put these eight columns
        on the first row of a tab called <strong>Holdings</strong>:
      </p>
      <table className="learn-table">
        <thead>
          <tr><th>Column</th><th>Header</th><th>Formula / Input</th></tr>
        </thead>
        <tbody>
          <tr><td>A</td><td>Ticker</td><td>Manual: KO, JNJ, etc.</td></tr>
          <tr><td>B</td><td>Shares</td><td>Manual</td></tr>
          <tr><td>C</td><td>Cost / Share</td><td>Manual (your average buy price)</td></tr>
          <tr><td>D</td><td>Total Cost</td><td><code>=B2*C2</code></td></tr>
          <tr><td>E</td><td>Current Price</td><td><code>=GOOGLEFINANCE(A2, &quot;price&quot;)</code></td></tr>
          <tr><td>F</td><td>Current Value</td><td><code>=B2*E2</code></td></tr>
          <tr><td>G</td><td>Annual Div / Share</td><td>Manual or <code>=E2*GOOGLEFINANCE(A2,&quot;yieldpct&quot;)/100</code></td></tr>
          <tr><td>H</td><td>Annual Income</td><td><code>=B2*G2</code></td></tr>
        </tbody>
      </table>
      <p>
        At the bottom of the sheet, add a <strong>TOTAL</strong> row with{' '}
        <code>=SUM(D:D)</code> for Total Cost, <code>=SUM(F:F)</code> for
        Current Value, and <code>=SUM(H:H)</code> for Annual Income.
      </p>

      <h2>Three extra columns that make it powerful</h2>
      <ul>
        <li>
          <strong>I: Yield</strong> = <code>=H2/F2*100</code> &mdash; current
          yield based on today&apos;s price.
        </li>
        <li>
          <strong>J: Yield on Cost</strong> = <code>=H2/D2*100</code> &mdash; the
          yield you locked in at purchase. Grows over time as dividends rise.
          See <Link to="/learn/yield-on-cost">Yield on Cost</Link>.
        </li>
        <li>
          <strong>K: Total Return %</strong> = <code>=(F2+SUMIFS(DividendLog!E:E,DividendLog!B:B,A2)-D2)/D2*100</code>{' '}
          &mdash; total return including all dividends collected to date. Pulls
          from a second tab covered below.
        </li>
      </ul>

      <h2>The Dividend Log tab (monthly income history)</h2>
      <p>
        A second tab called <strong>DividendLog</strong> tracks every dividend
        you actually receive. Columns:
      </p>
      <ol>
        <li><strong>A: Pay Date</strong></li>
        <li><strong>B: Ticker</strong></li>
        <li><strong>C: Shares Held</strong></li>
        <li><strong>D: Dividend / Share</strong></li>
        <li><strong>E: Total Received</strong> &nbsp;=<code>C*D</code></li>
        <li><strong>F: Account</strong> (Taxable / Roth / Traditional IRA)</li>
      </ol>
      <p>
        Every time a dividend hits your brokerage account, add a row. Then on a
        third tab called <strong>Monthly</strong>, use{' '}
        <code>=SUMIFS(DividendLog!E:E, DividendLog!A:A, &quot;&gt;=&quot;&amp;DATE(2026,1,1), DividendLog!A:A, &quot;&lt;&quot;&amp;DATE(2026,2,1))</code>{' '}
        to get January totals; one per month. A pivot table works even better
        if you&apos;re comfortable with them.
      </p>

      <h2>Live-price formulas: GOOGLEFINANCE vs Excel Stock Types</h2>
      <p>
        Google Sheets and Excel handle live market data differently:
      </p>
      <ul>
        <li>
          <strong>Google Sheets (free):</strong>{' '}
          <code>=GOOGLEFINANCE(&quot;KO&quot;,&quot;price&quot;)</code> gives a
          20-minute delayed price. Also supports <code>&quot;yieldpct&quot;</code>,
          <code>&quot;changepct&quot;</code>, <code>&quot;volume&quot;</code>, and
          <code>&quot;high52&quot;</code>. Misses dividend frequency, ex-dates,
          and dividend safety metrics. Data sometimes flakes on ADRs.
        </li>
        <li>
          <strong>Excel (Microsoft 365 only):</strong> Type a ticker, select the
          cell, and click <em>Data &rarr; Stocks</em>. Excel converts the cell
          into a rich Stock data type. You can then write{' '}
          <code>=A2.Price</code>, <code>=A2.&quot;52 week high&quot;</code>, etc.
          Microsoft 365 personal/family subscriptions only — standalone
          Excel doesn&apos;t have it.
        </li>
        <li>
          <strong>Older Excel / LibreOffice:</strong> No native option. Use a
          Python script with the <code>yfinance</code> package to refresh a
          CSV, then import on a schedule. Functional but fragile.
        </li>
      </ul>

      <h2>The four things spreadsheets get wrong</h2>
      <p>
        Spreadsheets are great until they aren&apos;t. Watch for these failure
        modes:
      </p>
      <ol>
        <li>
          <strong>Stale dividend rates.</strong> GOOGLEFINANCE returns the most
          recent <em>paid</em> dividend, not the freshly announced rate. A
          company that just raised its dividend looks unchanged for 3 months in
          your sheet.
        </li>
        <li>
          <strong>Special dividends silently included.</strong> Costco&apos;s
          $15 special dividend in 2024 inflated &quot;yieldpct&quot; for months,
          making future annual income look wildly wrong. See{' '}
          <Link to="/learn/special-dividends-in-cagr-math">special dividends
          in CAGR math</Link>.
        </li>
        <li>
          <strong>Dividend cuts not flagged.</strong> If AT&amp;T cuts its
          dividend 47%, your sheet shows the new lower rate the next pay-date.
          You won&apos;t see the cut as a cut, just a quietly lower annual
          income figure that you might not notice for a quarter.
        </li>
        <li>
          <strong>No forward-looking safety signals.</strong> Spreadsheets don&apos;t
          know about payout ratio trends, FCF coverage, or the
          {' '}<Link to="/learn/dividend-triangle">Dividend Triangle Score</Link>.
          Cuts often have 6&ndash;12 months of warning visible in fundamentals
          that a spreadsheet has no view into.
        </li>
      </ol>

      <h2>When to graduate from a spreadsheet</h2>
      <p>
        A spreadsheet is plenty if:
      </p>
      <ul>
        <li>You hold 1&ndash;5 tickers.</li>
        <li>Under ~$20K invested.</li>
        <li>You enjoy the manual upkeep of logging each dividend.</li>
        <li>You don&apos;t need forward-looking safety metrics or sector concentration warnings.</li>
      </ul>
      <p>
        It starts breaking when:
      </p>
      <ul>
        <li>You add a 10th ticker and start missing dividend-rate updates.</li>
        <li>You hold positions across taxable, Roth, and Traditional IRA accounts and want unified reporting with per-account-type tax treatment.</li>
        <li>You want ex-dividend alerts, monthly income forecasts, sector / yield-trap warnings, or rebalancing tools.</li>
        <li>You want to share your portfolio publicly without exposing dollar amounts (privacy-toggled share pages).</li>
      </ul>

      <h2>Importing your spreadsheet into DiviDrip</h2>
      <p>
        DiviDrip is free and accepts both .xlsx and .csv. The recommended
        path uses the official DiviDrip template — paste your positions in,
        save, drop the file back in:
      </p>
      <ol>
        <li>
          On the <Link to="/dashboard?view=portfolio">Portfolio dashboard</Link>, open the{' '}
          <strong>Capital Tracker card</strong> and click{' '}
          <strong>Download .xlsx template</strong>.
        </li>
        <li>
          Open the workbook in Excel or Google Sheets and go to the{' '}
          <strong>Holdings</strong> tab. Required columns:{' '}
          <strong>Ticker</strong> (col A), <strong>Shares</strong> (col C),{' '}
          <strong>Cost / Share</strong> (col D). Optional:{' '}
          <strong>Purchase Date</strong> (col P, ISO YYYY-MM-DD).
        </li>
        <li>
          Save the workbook and click <strong>Import CSV / XLSX</strong> in
          the Capital Tracker card.
        </li>
        <li>
          The modal previews every row, flags any tickers DiviDrip doesn&apos;t
          recognise, and lets you choose <strong>Append</strong> (add to
          existing positions, multiple cost lots per ticker allowed) or{' '}
          <strong>Overwrite</strong> (wipe and replace, cash + watchlist
          preserved).
        </li>
        <li>Click <strong>Confirm Import</strong>. Full portfolio loads in seconds.</li>
      </ol>
      <p>
        Already have a non-DiviDrip spreadsheet? Same flow with one extra
        step: export it to .csv first (Google Sheets:{' '}
        <em>File → Download → Comma-separated values</em>; Excel:{' '}
        <em>Save As → CSV UTF-8</em>), then drop the .csv into the same
        Import button. The modal accepts both extensions.
      </p>
      <p>
        Once imported, DiviDrip auto-populates yield, dividend frequency,
        payout ratio, Triangle Score, monthly income forecast, sector
        concentration, and everything else — you stop maintaining the
        manual sheet from this point.
      </p>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Try it</h2>
      <p>
        If you&apos;ve never tracked dividends in any way: build the 8-column
        Holdings tab above for your three biggest dividend holdings. Live for
        a month. When the manual logging starts to feel like a chore (and it
        will), open <Link to="/dashboard">DiviDrip</Link> and add the same
        positions — the dashboard will populate annual income, monthly
        income forecast, yield on cost, sector concentration, and the
        Dividend Triangle Score for each holding within 30 seconds. The
        spreadsheet was a great first step; the dashboard is the version that
        scales.
      </p>
    </LearnPage>
  );
}
